import React, { createContext, useContext, useState } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [selectedShippingMethod, setSelectedShippingMethod] = useState('self-collection');
  const hasMerch = cartItems.some(item => item.type !== 'ticket');
  const shippingMethod = hasMerch ? selectedShippingMethod : 'self-collection';
  const setShippingMethod = method => setSelectedShippingMethod(
    method === 'delivery' || method === 'tracked-mail' ? 'delivery' : 'self-collection'
  );

  const addToCart = (item) => {
    let wasAdded = true;
    
    setCartItems(prevItems => {
      // For tickets, always treat as single cart entry with quantity
      if (item.type === 'ticket') {
        const existingTicket = prevItems.find(i => i.id === item.id && i.type === 'ticket');
        if (existingTicket) {
          // Check if adding more would exceed the limit PER EVENT
          const newQuantity = existingTicket.quantity + item.quantity;
          if (newQuantity > 10) {
            alert('Maximum 10 tickets allowed per event!');
            wasAdded = false;
            return prevItems;
          }
          // Update existing ticket quantity for this specific event
          return prevItems.map(i =>
            i.id === item.id && i.type === 'ticket'
              ? { ...i, quantity: newQuantity }
              : i
          );
        }
        // Add new ticket for a different event (with specified quantity, max 10)
        const quantityToAdd = Math.min(item.quantity, 10);
        return [...prevItems, { ...item, quantity: quantityToAdd }];
      }
      
      // For other items (merchandise), allow multiple with size variants
      const existingItem = prevItems.find(i => i.id === item.id && i.size === item.size);
      if (existingItem) {
        return prevItems.map(i =>
          i.id === item.id && i.size === item.size
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        );
      }
      return [...prevItems, item];
    });
    
    return wasAdded;
  };

  const getCartCount = () => {
    // Count tickets as 1 item regardless of quantity, count merchandise normally
    return cartItems.reduce((count, item) => {
      if (item.type === 'ticket') {
        return count + 1; // Each ticket entry counts as 1 item
      }
      return count + item.quantity; // Merchandise counts by quantity
    }, 0);
  };

  const removeFromCart = (itemId, size = null) => {
    setCartItems(prevItems => 
      prevItems.filter(item => {
        // For tickets (no size), just match by ID
        if (item.type === 'ticket') {
          return item.id !== itemId;
        }
        // For merchandise, match by ID and size
        return !(item.id === itemId && (size === null || item.size === size));
      })
    );
  };

  const updateQuantity = (itemId, size, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId, size);
      return;
    }
    
    // For tickets, enforce max 10 limit
    const item = cartItems.find(i => i.id === itemId && (i.type === 'ticket' || i.size === size));
    if (item && item.type === 'ticket' && newQuantity > 10) {
      alert('Maximum 10 tickets allowed per event!');
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item => {
        // For tickets, only match by ID (ignore size parameter)
        if (item.type === 'ticket' && item.id === itemId) {
          return { ...item, quantity: Math.min(newQuantity, 10) };
        }
        // For merchandise, match by ID and size
        if (item.id === itemId && item.size === size) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setSelectedShippingMethod('self-collection');
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      shippingMethod,
      setShippingMethod,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
